import { useEffect } from "react";
import { useSessionProfile } from "./hooks/use-session-profile";
import { useLocation as useWouterLocation } from "wouter";
import { Switch, Route, Redirect } from "wouter";
import Dashboard from "@/pages/Dashboard";
import Headlines from "@/pages/Headlines";
import Pages from "@/pages/Pages";
import TaskBoards from "@/pages/TaskBoards";
import ComingSoon from "@/pages/ComingSoon";
import Calendars from "@/pages/Calendars";
import MeetingAgendas from "@/pages/MeetingAgendas";
import LiveMeeting from "@/pages/LiveMeeting";
import Performance from "@/pages/Performance";
import Team from "@/pages/Team";
import Profile from "@/pages/Profile";
import Coaching from "@/pages/Coaching";
import Templates from "@/pages/Templates";
import NetPromoterScore from "@/pages/NetPromoterScore";
import NPSSurveyCall from "@/pages/NPSSurveyCall";
import Settings from "@/pages/Settings";
import StorageManager from "@/pages/StorageManager";
import RecoveryTiers from "@/pages/RecoveryTiers";
import RetentionControls from "@/pages/RetentionControls";
import BillingUpgrade from "@/pages/BillingUpgrade";
import Help from "@/pages/Help";
import Login from "@/pages/Login";
import NotFound from "@/pages/not-found";
import { MainLayout } from "@/components/layouts/MainLayout";
import { UserProfile } from "@shared/types";
import { useToast } from "@/hooks/use-toast";
// Update LocationProvider import to make sure we're using the latest version
import { LocationProvider } from "@/contexts/LocationContext";
import { OrganizationProvider } from "@/contexts/OrganizationContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
// import { AuthenticationMonitor } from './components/AuthenticationMonitor';
import { OrganizationSwitchLoader } from './components/OrganizationSwitchLoader';
import PEDashboard from "@/components/PEDashboard";
import FinancialDashboard from "@/components/FinancialDashboard";

import { loginReturnPath } from "./lib/login-return-path";

function App() {
  const { user, loading, sessionExpired, acceptSession, suspendRefresh } = useSessionProfile();
  const { toast } = useToast();
  const [location, setLocation] = useWouterLocation();

  useEffect(() => {
    if (user && location === '/login') {
      try {
        const lastPath = localStorage.getItem('lastPath') || '/';
        setLocation(loginReturnPath('/login', lastPath));
      } catch {
        setLocation('/');
      }
    }
  }, [user, location, setLocation]);

  const handleLogin = (userData: UserProfile) => {
    // The login card renders at the interrupted URL. Keep that tab's route,
    // including query/hash, ahead of another tab's persisted lastPath.
    let savedPath: string | null = null;
    try { savedPath = localStorage.getItem('lastPath'); } catch {}
    const returnPath = loginReturnPath(
      window.location.pathname + window.location.search + window.location.hash, savedPath,
    );
    acceptSession(userData);
    setLocation(returnPath);
  };

  const handleLogout = async () => {
    // Invalidate pending profile reads before waiting for cookie cleanup.
    suspendRefresh();
    try {

      // Call server logout to clear auth cookies/session; fallback to force-logout.
      try {
        await fetch('/api/auth/logout', {
          method: 'POST',
          credentials: 'include',
          redirect: 'error',
        });
      } catch (_error) {
        await fetch('/api/auth/force-logout', {
          method: 'POST',
          credentials: 'include',
          redirect: 'error',
        }).catch(() => undefined);
      }

      // Clear local user state only after attempting server-side cookie cleanup
      acceptSession(null);
      setLocation('/login');
      
      // Show success message
      toast({
        title: "Logged Out",
        description: "You have been successfully logged out",
      });
      
      console.log('Logout completed successfully');
    } catch (error) {
      console.error('Logout error:', error);
      // Even if there's an error, still log out locally
      acceptSession(null);
      setLocation('/login');
    }
  };

  const ProfileRedirect = () => {
    useEffect(() => {
      if (user?.id) {
        setLocation(`/profile/${user.id}`);
      }
    }, [setLocation, user?.id]);
    return <div className="p-6">Loading profile…</div>;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <ErrorBoundary>
      <Switch>
        {!user ? (
          <Route path="*">
            <Login onLogin={handleLogin} sessionExpired={sessionExpired} />
          </Route>
        ) : (
          <OrganizationProvider user={user}>
            <OrganizationSwitchLoader />
            <LocationProvider user={user}>
              {/* Track and persist last visited path for post-login redirects */}
              { }
              {(() => {
                try {
                  if (location && location !== '/login') {
                    localStorage.setItem('lastPath', location);
                  }
                } catch {}
                return null as any;
              })()}
              <Switch>
              <Route path="/">
                <MainLayout user={user} onLogout={handleLogout}>
                  <Dashboard user={user} />
                </MainLayout>
              </Route>
              <Route path="/headlines">
                <MainLayout user={user} onLogout={handleLogout}>
                  <Headlines user={user} />
                </MainLayout>
              </Route>
              <Route path="/pages">
                <MainLayout user={user} onLogout={handleLogout}>
                  <Pages user={user} />
                </MainLayout>
              </Route>
              <Route path="/task-boards">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="Task Boards" />
                </MainLayout>
              </Route>
              <Route path="/stickystack">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="StickyStack" description="Federated board experience replacing Task Boards." />
                </MainLayout>
              </Route>
              <Route path="/calendars">
                <MainLayout user={user} onLogout={handleLogout}>
                  <Calendars user={user} />
                </MainLayout>
              </Route>
              <Route path="/meeting-agendas">
                <MainLayout user={user} onLogout={handleLogout}>
                  <MeetingAgendas user={user} />
                </MainLayout>
              </Route>
              <Route path="/agenda">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="My Agenda" />
                </MainLayout>
              </Route>
              <Route path="/workspace">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="My Workspace" />
                </MainLayout>
              </Route>
              <Route path="/inbox">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="My Inbox" />
                </MainLayout>
              </Route>
              <Route path="/favorites">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="Favorites" />
                </MainLayout>
              </Route>
              <Route path="/dm">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="Direct Messages" />
                </MainLayout>
              </Route>
              <Route path="/calls">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="Calls" description="Video, screenshare, and recording." />
                </MainLayout>
              </Route>
              <Route path="/ai">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="Bolt AI" description="Secure chat, shared projects, and HIPAA-aware document intelligence. Database hardening gates must close before launch." />
                </MainLayout>
              </Route>
              <Route path="/meetings/:id/live">
                <MainLayout user={user} onLogout={handleLogout}>
                  <LiveMeeting user={user} />
                </MainLayout>
              </Route>
              <Route path="/performance">
                <MainLayout user={user} onLogout={handleLogout}>
                  <Performance user={user} />
                </MainLayout>
              </Route>
              <Route path="/pe-dashboard">
                <MainLayout user={user} onLogout={handleLogout}>
                  <PEDashboard />
                </MainLayout>
              </Route>
              <Route path="/financial-dashboard">
                <MainLayout user={user} onLogout={handleLogout}>
                  <FinancialDashboard user={user} />
                </MainLayout>
              </Route>
              <Route path="/team">
                <MainLayout user={user} onLogout={handleLogout}>
                  <Team user={user} />
                </MainLayout>
              </Route>
              <Route path="/coaching">
                <MainLayout user={user} onLogout={handleLogout}>
                  <Coaching user={user} />
                </MainLayout>
              </Route>
              <Route path="/button-your-buttons">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="Button your Buttons" />
                </MainLayout>
              </Route>
              <Route path="/structure">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="Structure Boards" />
                </MainLayout>
              </Route>
              <Route path="/meetings">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="Meetings" description="Daily, Weekly, Quarterly, Yearly, Recharge, Daily 5/KPI, Coaching" />
                </MainLayout>
              </Route>
              <Route path="/passwords">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="Passwords" />
                </MainLayout>
              </Route>
              <Route path="/shopping-list">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="Shopping List" />
                </MainLayout>
              </Route>
              <Route path="/contacts">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="Important Contacts" />
                </MainLayout>
              </Route>
              <Route path="/referrals">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="Referral Tracking" />
                </MainLayout>
              </Route>
              <Route path="/profile/:id">
                <MainLayout user={user} onLogout={handleLogout}>
                  <Profile user={user} />
                </MainLayout>
              </Route>
              <Route path="/profile">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ProfileRedirect />
                </MainLayout>
              </Route>
              <Route path="/templates">
                <MainLayout user={user} onLogout={handleLogout}>
                  <Templates user={user} />
                </MainLayout>
              </Route>
              <Route path="/nps">
                <MainLayout user={user} onLogout={handleLogout}>
                  <NetPromoterScore />
                </MainLayout>
              </Route>
              <Route path="/nps-survey-call">
                <MainLayout user={user} onLogout={handleLogout}>
                  <NPSSurveyCall />
                </MainLayout>
              </Route>
              <Route path="/staffing">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="Staffing & Doctor Scheduling" />
                </MainLayout>
              </Route>
              <Route path="/leadership-ai">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="Next-Gen AI Leadership Program" />
                </MainLayout>
              </Route>
              <Route path="/settings">
                <MainLayout user={user} onLogout={handleLogout}>
                  <Settings user={user} />
                </MainLayout>
              </Route>
              <Route path="/settings/account">
                <MainLayout user={user} onLogout={handleLogout}>
                  <Settings user={user} />
                </MainLayout>
              </Route>
              <Route path="/settings/notifications">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="Notifications" />
                </MainLayout>
              </Route>
              <Route path="/settings/out-of-office">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="Out Of Office Status" />
                </MainLayout>
              </Route>
              <Route path="/settings/integrations">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="Integrations" />
                </MainLayout>
              </Route>
              <Route path="/settings/trash">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="Trash" />
                </MainLayout>
              </Route>
              <Route path="/billing/recovery-tiers">
                <MainLayout user={user} onLogout={handleLogout}>
                  <RecoveryTiers />
                </MainLayout>
              </Route>
              <Route path="/billing/retention-controls">
                <MainLayout user={user} onLogout={handleLogout}>
                  <RetentionControls />
                </MainLayout>
              </Route>
              <Route path="/billing/upgrade">
                <MainLayout user={user} onLogout={handleLogout}>
                  <BillingUpgrade />
                </MainLayout>
              </Route>
              <Route path="/support"><Redirect to="/help" /></Route>
              <Route path="/help">
                <MainLayout user={user} onLogout={handleLogout}>
                  <Help />
                </MainLayout>
              </Route>
              <Route path="/dictation">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="Doctor Dictation" />
                </MainLayout>
              </Route>
              <Route path="/assistant">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="AI Leadership Assistance" />
                </MainLayout>
              </Route>
              <Route path="/utilization">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="Percent Utilization" />
                </MainLayout>
              </Route>
              <Route path="/talent">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="Talent Pipeline" />
                </MainLayout>
              </Route>
              <Route path="/financing">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="Patient Financing" />
                </MainLayout>
              </Route>
              <Route path="/odoo">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="Odoo Accounting" />
                </MainLayout>
              </Route>
              <Route path="/audit">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="Audit" />
                </MainLayout>
              </Route>
              <Route path="/billing">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="Billing" />
                </MainLayout>
              </Route>
              <Route path="/settings/storage-manager">
                <MainLayout user={user} onLogout={handleLogout}>
                  <StorageManager />
                </MainLayout>
              </Route>
              <Route path="/call-center">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="Call Center" />
                </MainLayout>
              </Route>
              <Route path="/marketing">
                <MainLayout user={user} onLogout={handleLogout}>
                  <ComingSoon title="Social/Marketing Studio" />
                </MainLayout>
              </Route>
              <Route path="*">
                <MainLayout user={user} onLogout={handleLogout}>
                  <NotFound />
                </MainLayout>
              </Route>
              </Switch>
            </LocationProvider>
          </OrganizationProvider>
        )}
      </Switch>
      {/* Authentication Monitor for development debugging - DISABLED */}
      {/* {process.env.NODE_ENV === 'development' && <AuthenticationMonitor />} */}
    </ErrorBoundary>
  );
}

export default App;
